import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, } from "react";
import { storage } from "@/utils/storage";
import { Role, DEFAULT_STAFF_ACCESS, mapAuthContactToUser } from "@/types/auth.types";
import { authService } from "@/api/services/auth.service";
// Development auto-login users
const DEV_ADMIN_USER = {
    id: "dev-admin-123",
    email: "test@intent.com",
    name: "test",
    role: Role.ADMIN,
};
const DEV_CLIENT_USER = {
    id: "dev-client-123",
    email: "client@intent.com",
    name: "Test Client",
    role: Role.CLIENT,
};
const DEV_SURVEYOR_USER = {
    id: "dev-surveyor-123",
    email: "surveyor@intent.com",
    name: "Test Surveyor",
    role: Role.SURVEYOR,
};
const DEV_CONTRACTOR_USER = {
    id: "dev-contractor-123",
    email: "contractor@intent.com",
    name: "Test Contractor",
    role: Role.CONTRACTOR,
};
const DEV_SUBCONTRACTOR_USER = {
    id: "dev-subcontractor-123",
    email: "subcontractor@intent.com",
    name: "Test Subcontractor",
    role: Role.SUBCONTRACTOR,
};
const DEV_TOKEN = "dev-admin-token-123456789";
const DEV_CLIENT_TOKEN = "dev-client-token-123456789";
const DEV_SURVEYOR_TOKEN = "dev-surveyor-token-123456789";
const DEV_CONTRACTOR_TOKEN = "dev-contractor-token-123456789";
const DEV_SUBCONTRACTOR_TOKEN = "dev-subcontractor-token-123456789";
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasOriginalAccount, setHasOriginalAccount] = useState(false);
    const performLogin = (token, userData) => {
        storage.setToken(token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
    };
    useEffect(() => {
        // Check for development auto-login
        const urlParams = new URLSearchParams(window.location.search);
        const autoLogin = urlParams.get("autoLogin");
        const isDevelopment = process.env.NODE_ENV === "development";
        const isLocalhost = window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
        // Allow auto-login in development mode or when running on localhost
        const allowAutoLogin = isDevelopment || isLocalhost;
        if (autoLogin === "admin" && allowAutoLogin) {
            // Auto-login as admin for development
            performLogin(DEV_TOKEN, DEV_ADMIN_USER);
            setIsLoading(false);
            return;
        }
        if (autoLogin === "client" && allowAutoLogin) {
            // Auto-login as client for development
            performLogin(DEV_CLIENT_TOKEN, DEV_CLIENT_USER);
            setIsLoading(false);
            return;
        }
        if (autoLogin === "surveyor" && allowAutoLogin) {
            // Auto-login as surveyor for development
            performLogin(DEV_SURVEYOR_TOKEN, DEV_SURVEYOR_USER);
            setIsLoading(false);
            return;
        }
        if (autoLogin === "contractor" && allowAutoLogin) {
            // Auto-login as contractor for development
            performLogin(DEV_CONTRACTOR_TOKEN, DEV_CONTRACTOR_USER);
            setIsLoading(false);
            return;
        }
        if (autoLogin === "subcontractor" && allowAutoLogin) {
            // Auto-login as subcontractor for development (uses contractor area)
            performLogin(DEV_SUBCONTRACTOR_TOKEN, DEV_SUBCONTRACTOR_USER);
            setIsLoading(false);
            return;
        }
        // Normal authentication flow
        const token = storage.getToken();
        if (token) {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                setUser(JSON.parse(storedUser));
            }
        }
        setHasOriginalAccount(storage.hasOriginalAccount());
        setIsLoading(false);
    }, []);
    // Removed auto-refresh effects - permissions are now included in JWT payload
    // and validated efficiently without database queries on each request
    const login = (token, userData) => {
        storage.setToken(token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        setHasOriginalAccount(storage.hasOriginalAccount());
    };
    const logout = () => {
        storage.clear();
        setUser(null);
        setHasOriginalAccount(false);
    };
    const switchToClientView = (token, clientUser) => {
        // Store current account info before switching
        const currentToken = storage.getToken();
        const currentUser = localStorage.getItem("user");
        if (currentToken && currentUser) {
            storage.setOriginalAccount(currentToken, currentUser);
        }
        // Switch to client account
        storage.setToken(token);
        localStorage.setItem("user", JSON.stringify(clientUser));
        setUser(clientUser);
        setHasOriginalAccount(true);
    };
    const backToOriginalAccount = () => {
        const originalAccount = storage.getOriginalAccount();
        if (originalAccount) {
            storage.setToken(originalAccount.token);
            localStorage.setItem("user", originalAccount.user);
            setUser(JSON.parse(originalAccount.user));
            storage.clearOriginalAccount();
            setHasOriginalAccount(false);
        }
    };
    const refreshUserAccess = async () => {
        if (!user)
            return;
        try {
            const response = await authService.refreshToken();
            if (response.data.success && response.data.token) {
                const updatedUser = response.data.contact
                    ? mapAuthContactToUser(response.data.contact)
                    : response.data.user;
                if (updatedUser) {
                    storage.setToken(response.data.token);
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    setUser(updatedUser);
                }
            }
        }
        catch {
            // Token refresh failed; user keeps current session until it expires
        }
    };
    // User access is now computed directly in hasAccess function for reactivity
    const hasAccess = (key) => {
        if (!user)
            return false;
        // Get current access permissions (reactive to user changes)
        const currentAccess = user.access || DEFAULT_STAFF_ACCESS;
        // For admin users, check if they have explicit access restrictions
        // If access is explicitly set to false, deny access
        if (user.role === Role.ADMIN) {
            // If user.access exists and the key is explicitly set to false, deny access
            if (user.access && user.access[key] === false) {
                return false;
            }
            return true;
        }
        // For staff users, never grant access via 'all' flag - check individual permissions only
        if (user.role === Role.STAFF) {
            return currentAccess[key] === true;
        }
        return currentAccess.all || currentAccess[key];
    };
    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        isAdmin: user?.role === Role.ADMIN,
        isStaff: user?.role === Role.STAFF,
        isClient: user?.role === Role.CLIENT,
        isSurveyor: user?.role === Role.SURVEYOR,
        isContractor: user?.role === Role.CONTRACTOR || user?.role === Role.SUBCONTRACTOR,
        hasOriginalAccount,
        hasAccess,
        login,
        logout,
        switchToClientView,
        backToOriginalAccount,
        refreshUserAccess,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};
